import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Layers } from "lucide-react";
import api from "@/lib/api";
import { fmtNum, fmtPct } from "@/lib/format";
import { ShiftTrend } from "@/components/ShiftTrend";

const BAR = { green: "bg-emerald-500", amber: "bg-amber-500", red: "bg-rose-500" };
const TXT = { green: "text-emerald-700", amber: "text-amber-700", red: "text-rose-700" };

export function ShiftPlanSplit({ date, plantId }) {
  const q = useQuery({ queryKey: ["daily-plan-summary", date, plantId], queryFn: async () => (await api.get("/daily-plan/summary", { params: { reporting_date: date, plant_id: plantId || undefined } })).data, refetchInterval: 60_000 });
  const shifts = q.data?.by_shift || [];
  if (!q.data || !shifts.length) return null;
  const total = q.data;
  return (
    <div className="pp-card p-4 rise rise-2" data-testid="shift-plan-split">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="pp-label flex items-center gap-2"><Layers size={14} /> Plan vs Actual by shift · {date}</div>
        <div className="flex items-center gap-3 text-xs">
          <span className={`font-mono font-semibold ${TXT[total.status] || "text-slate-600"}`} data-testid="shift-split-total">Day {fmtNum(total.pack_actual)} / {fmtNum(total.pack_plan)} · {total.achieve_pct != null ? fmtPct(total.achieve_pct) : "—"}</span>
          <Link to="/performance" className="pp-btn-ghost h-7 px-2 text-xs" data-testid="shift-split-link">Plan vs Actual</Link>
        </div>
      </div>
      <div className={`grid gap-3 ${shifts.length > 2 ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
        {shifts.map((g) => {
          const pct = Math.min(100, g.achieve_pct || 0);
          return (
            <div key={g.shift} className="rounded-md border border-slate-200 p-3" data-testid={`shift-split-${g.shift}`}>
              <div className="flex items-baseline justify-between">
                <div className="text-sm font-bold">Shift {g.shift}</div>
                <div className={`text-xs font-mono font-semibold ${TXT[g.status] || "text-slate-500"}`}>{g.achieve_pct != null ? fmtPct(g.achieve_pct) : "—"}</div>
              </div>
              <div className="mt-1 text-2xl font-bold font-mono leading-none">{fmtNum(g.pack_actual)}<span className="text-sm text-slate-400 font-normal"> / {fmtNum(g.pack_plan)} pcs</span></div>
              <div className="mt-2 h-2 w-full rounded-full bg-slate-200 overflow-hidden"><div className={`h-full ${BAR[g.status] || "bg-slate-400"} transition-[width] duration-500`} style={{ width: `${pct}%` }} /></div>
              <div className="mt-1 flex justify-between text-[11px] text-slate-500">
                <span>{g.entries} plan row(s){g.scope === "day" ? " · day-level actual" : ""}</span>
                <span className={`font-mono ${g.variance < 0 ? "text-rose-600" : "text-emerald-700"}`} data-testid={`shift-split-gap-${g.shift}`}>{g.variance >= 0 ? "+" : ""}{fmtNum(g.variance)} gap</span>
              </div>
            </div>
          );
        })}
      </div>
      <ShiftTrend date={date} plantId={plantId} />
    </div>
  );
}
